import * as React from "react"

import { cn } from "@/lib/utils"

import { SketchBorder } from "./border"
import { SketchFill } from "./fill"
import { sketchTones, type SketchTone } from "./tones"

export { SketchBorder } from "./border"
export { SketchSprite } from "./sprite"
export type { SketchTone } from "./tones"

// Host must be a positioned box and isolate the texture blend. Override the corner size per
// consumer with `[--sketch-cap:16px]`; the fill radius follows (outer radius ~0.9 cap, minus 1px inset).
// On 1x-1.5x screens the ~1.3px hand-drawn outline is a two-row antialiased smear; the boost
// thickens it by 0.6 CSS px so it reads as a solid line. Retina (2x+) renders it untouched.
export const sketchHostClass =
  "relative isolate [--sketch-cap:7px] [--sketch-fill-radius:calc(var(--sketch-cap)*0.83_-_1px)] [@media(max-resolution:1.5dppx)]:[--sketch-stroke-boost:0.6px]"

type SketchLayersProps = {
  tone: SketchTone
}

// Fill + border only; for hosts that are already the box (e.g. <button>). Content must be `relative z-10`.
export function SketchLayers({ tone }: SketchLayersProps) {
  const skin = sketchTones[tone]

  return (
    <>
      <SketchFill skin={skin} />
      <SketchBorder className={skin.border} />
    </>
  )
}

export type SketchFrameProps = React.ComponentPropsWithoutRef<"div"> & {
  tone?: SketchTone
  contentClassName?: string
}

// Plate: wraps arbitrary content in the hand-drawn frame.
export const SketchFrame = React.forwardRef<HTMLDivElement, SketchFrameProps>(
  ({ tone = "muted", className, contentClassName, children, ...props }, ref) => (
    <div ref={ref} className={cn(sketchHostClass, className)} {...props}>
      <SketchLayers tone={tone} />
      <div className={cn("relative z-10", contentClassName)}>{children}</div>
    </div>
  ),
)
SketchFrame.displayName = "SketchFrame"
